import { Pipe, PipeTransform, inject } from '@angular/core'
import { AuditLog, UserType } from '@generated/sammathnaur.graphql'
import { TranslateService } from '@ngx-translate/core'
import { constantCase } from 'change-case'

@Pipe({
    name: 'translateAuditLogEventPipe',
    standalone: false
})
export class TranslateAuditLogEventPipe implements PipeTransform {
  private readonly translateService = inject(TranslateService)
  private readonly mappedKeys = {
    'user-created': 'TRANSLATE_AUDIT_LOG_EVENT.USER.CREATED',
    'user-archived': 'TRANSLATE_AUDIT_LOG_EVENT.USER.ARCHIVED',
    'user-unarchived': 'TRANSLATE_AUDIT_LOG_EVENT.USER.UNARCHIVED',
    'user-archived-yourself': 'TRANSLATE_AUDIT_LOG_EVENT.USER.ARCHIVED_YOURSELF',
    'user-unarchived-yourself': 'TRANSLATE_AUDIT_LOG_EVENT.USER.UNARCHIVED_YOURSELF',
    'user-enabled-preference': 'TRANSLATE_AUDIT_LOG_EVENT.USER.UPDATED.PREFERENCE.ENABLED',
    'user-disabled-preference': 'TRANSLATE_AUDIT_LOG_EVENT.USER.UPDATED.PREFERENCE.DISABLED',
    'user-enabled-preference-yourself': 'TRANSLATE_AUDIT_LOG_EVENT.USER.UPDATED.PREFERENCE.ENABLED_YOURSELF',
    'user-disabled-preference-yourself': 'TRANSLATE_AUDIT_LOG_EVENT.USER.UPDATED.PREFERENCE.DISABLED_YOURSELF',
    'user-enabled-mfa-yourself': 'TRANSLATE_AUDIT_LOG_EVENT.USER.UPDATED.MFA.ENABLED',
    'user-disabled-mfa-yourself': 'TRANSLATE_AUDIT_LOG_EVENT.USER.UPDATED.MFA.DISABLED',
    'user-updated-type': 'TRANSLATE_AUDIT_LOG_EVENT.USER.UPDATED.TYPE.CHANGED',
    'user-updated-password': 'TRANSLATE_AUDIT_LOG_EVENT.USER.UPDATED.PASSWORD.CHANGED',
    'user-updated-type-yourself': 'TRANSLATE_AUDIT_LOG_EVENT.USER.UPDATED.TYPE.CHANGED_YOURSELF',
    'user-updated-password-yourself': 'TRANSLATE_AUDIT_LOG_EVENT.USER.UPDATED.PASSWORD.CHANGED_YOURSELF',
    'resetPasswordEmail-sent-success': 'TRANSLATE_AUDIT_LOG_EVENT.RESET_PASSWORD_EMAIL.SENT.SUCCESS',
    'resetPasswordEmail-sent-userRequestedEmail-success': 'TRANSLATE_AUDIT_LOG_EVENT.RESET_PASSWORD_EMAIL.SENT.USERREQUESTEDEMAIL.SUCCESS',
    'resetPasswordEmail-sent-error': 'TRANSLATE_AUDIT_LOG_EVENT.RESET_PASSWORD_EMAIL.SENT.ERROR',
    'resetPasswordEmail-sent-userRequestedEmail-error': 'TRANSLATE_AUDIT_LOG_EVENT.RESET_PASSWORD_EMAIL.SENT.USERREQUESTEDEMAIL.ERROR',
    'resetPasswordEmail-sent-Delivery-success': 'TRANSLATE_AUDIT_LOG_EVENT.RESET_PASSWORD_EMAIL.SENT.DELIVERY.SUCCESS',
    'resetPasswordEmail-sent-Send-success': 'TRANSLATE_AUDIT_LOG_EVENT.RESET_PASSWORD_EMAIL.SENT.SEND.SUCCESS',
  }

  public transform(auditLog?: AuditLog): string {
    if (!auditLog) {
      return ''
    }

    const translationResult = this.toTranslate(auditLog)
    if (!translationResult) {
      return ''
    }

    return this.translateService.instant(translationResult.key, translationResult.params)
  }

  private toTranslate(auditLog: AuditLog): { key: string, params?: Record<string, string> } | null {
    const translateKey = [
      auditLog.type,
      auditLog.action,
      auditLog.type !== 'resetPasswordEmail' && !auditLog.metadata?.preference && auditLog.metadata?.change,
      auditLog.type !== 'resetPasswordEmail' && auditLog.metadata?.preference && 'preference',
      auditLog.type !== 'resetPasswordEmail' && auditLog.metadata?.user?.id === auditLog.createdBy.id && 'yourself',
      auditLog.metadata?.responseFromSesType,
      auditLog.metadata?.emailStatus === 'success' ? 'success' : auditLog.metadata?.emailStatus === 'error' ? 'error' : null,
    ].filter(Boolean).join('-')

    const translateKeyValue = this.mappedKeys[translateKey as keyof typeof this.mappedKeys]
    const translatePreferenceKey = auditLog.metadata?.preference && `TRANSLATE_AUDIT_LOG_EVENT.${auditLog.type.toUpperCase()}.UPDATED.PREFERENCE.OPTIONS.${ constantCase(auditLog.metadata?.preference) }`
    const translateTypeKey = auditLog.type === 'user' && `TRANSLATE_AUDIT_LOG_EVENT.${auditLog.type.toUpperCase()}.UPDATED.TYPE.OPTIONS.${
      constantCase(auditLog.metadata?.user?.type as UserType || '')
    }`

    const params = {
      username: auditLog.metadata?.user?.name || '',
      preference: translatePreferenceKey && this.translateService.instant(translatePreferenceKey),
      type: translateTypeKey && this.translateService.instant(translateTypeKey),
    }

    return translateKeyValue
      ? { key: translateKeyValue, params }
      : null
  }
}
